import React from 'react'
import {FormattedNumber} from 'react-intl'
import {css} from '@emotion/react'
import styled from '@emotion/styled'

import {NoStyleButton} from '@daedalus/atlas/helpers/NoStyleButton'
import {Icon} from '@daedalus/atlas/Icon'
import {BasicTagWrapper, Tag} from '@daedalus/atlas/Tag'
import {Text} from '@daedalus/atlas/Text'
import {mq} from '@daedalus/atlas/utils/breakpoints'
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage'
import {applyRoundingStrategy} from '@daedalus/core/src/utils/number'

type VariantType = 'withInfo' | 'withoutInfo' | 'valueOnly'
interface Props {
  className?: string
  variant: VariantType
  onClick?: (e: React.MouseEvent) => void
  redeemValue?: number
  getValue?: number
  accountValue?: number
  currency?: string
  onMouseEnter?: (e: React.MouseEvent) => void
  onMouseLeave?: (e: React.MouseEvent) => void
  allowClickPropagation?: boolean
}

/**
 * @deprecated
 * This component and directory is deprecated and will be removed soon.
 */

const WrapperButton = styled(NoStyleButton)<{
  hasValue?: boolean
  variant: VariantType
  allowClickPropagation?: boolean
}>(
  ({theme, hasValue, variant, allowClickPropagation}) => css`
    ${allowClickPropagation &&
    `
      pointer-events: none;
    `}
    display: inline-flex;
    align-items: center;
    border-radius: ${theme.layout.radius.md}px;
    background-color: ${theme.colors.background.success.c100};
    cursor: ${variant === 'withInfo' ? 'pointer' : 'default'};
    padding-left: ${theme.layout.spacing.s100}px;
    padding-right: ${variant === 'withInfo' || hasValue
      ? 0
      : theme.layout.spacing.s100}px;
    transition: background-color 0.2s, color 0.2s;
    &:hover {
      background-color: ${theme.colors.background.success.c200};
      ${BasicTagWrapper} {
        background-color: ${theme.colors.background.success.c200};
        color: ${theme.colors.content.success.c900};
      }
      ${CashbackTagValue} {
        background-color: ${theme.colors.background.success.c300};
        color: ${theme.colors.content.success.c900};
        &:before {
          border-color: transparent transparent transparent
            ${theme.colors.background.success.c200};
        }
      }
    }
    &:active {
      background-color: ${theme.colors.background.success.c300};
      ${BasicTagWrapper} {
        background-color: ${theme.colors.background.success.c300};
        color: ${theme.colors.content.success.c900};
      }
      ${CashbackTagValue} {
        background-color: ${theme.colors.background.success.c400};
        color: ${theme.colors.content.success.c900};
        &:before {
          border-color: transparent transparent transparent
            ${theme.colors.border.success.c300};
        }
      }
    }
    &:disabled,
    &:disabled:hover,
    &:disabled:active {
      background-color: ${theme.colors.background.success.c100};
      ${BasicTagWrapper} {
        background-color: ${theme.colors.background.success.c100};
        color: ${theme.colors.content.success.c900};
      }
      ${CashbackTagValue} {
        background-color: ${theme.colors.background.success.c200};
        color: ${theme.colors.content.success.c900};
        &:before {
          border-color: transparent transparent transparent
            ${theme.colors.border.success.c100};
        }
      }
    }
    ${mq.desktopXs(css`
      &:active {
        background-color: ${theme.colors.background.success.c200};
        ${BasicTagWrapper} {
          background-color: ${theme.colors.background.success.c200};
          color: ${theme.colors.content.success.c900};
        }
        ${CashbackTagValue} {
          background-color: ${theme.colors.background.success.c300};
          color: ${theme.colors.content.success.c900};
          &:before {
            border-color: transparent transparent transparent
              ${theme.colors.border.success.c200};
          }
        }
      }
    `)}
  `
)

const CashbackTagText = styled('div')`
  white-space: nowrap;
`

const CashbackTagValue = styled.div<{variant: VariantType}>(
  ({theme, variant}) => css`
    display: flex;
    position: relative;
    align-items: center;
    align-self: stretch;
    padding-top: ${variant === 'valueOnly'
      ? `${theme.layout.spacing.s000}px`
      : `${theme.layout.spacing.s200}px`};
    padding-right: ${variant === 'valueOnly'
      ? `${theme.layout.spacing.s200}px`
      : `${theme.layout.spacing.s300}px`};
    padding-bottom: ${variant === 'valueOnly'
      ? `${theme.layout.spacing.s000}px`
      : `${theme.layout.spacing.s200}px`};
    padding-left: ${variant === 'valueOnly'
      ? `${theme.layout.spacing.s200}px`
      : `${theme.layout.spacing.s400}px`};
    border-radius: ${variant === 'valueOnly'
      ? `${theme.layout.radius.md}px`
      : `0 ${theme.layout.radius.md}px ${theme.layout.radius.md}px 0`};
    background-color: ${theme.colors.background.success.c200};
    color: ${theme.colors.content.success.c900};
    margin-left: ${variant === 'withInfo' ? -theme.layout.spacing.s100 : 0}px;
    transition: background-color 0.2s, color 0.2s;

    ${variant !== 'valueOnly' &&
    `
    &:before {
      content: '';
      position: absolute;
      width: 0;
      height: 0;
      left: 0;
      border-style: solid;
      border-width: 12px 0 12px 5px;
      border-color: transparent transparent transparent
        ${theme.colors.background.success.c100};
      transition: border-color 0.2s;
    }
    `}
  `
)

const Value = ({value, currency}: {value?: number; currency?: string}) => {
  if (!value || !currency) return null
  return (
    <FormattedNumber
      minimumFractionDigits={0}
      maximumFractionDigits={0}
      value={applyRoundingStrategy('floor', value)}
      style="currency"
      currency={currency}
    />
  )
}

export const CashbackTag = ({
  variant,
  redeemValue,
  getValue,
  accountValue,
  currency,
  onClick,
  className,
  onMouseEnter,
  onMouseLeave,
  allowClickPropagation = false
}: Props) => {
  const value = redeemValue || getValue || accountValue
  const hasInfo = variant === 'withInfo'
  const hasValue = Boolean(value && value > 0)
  const showRedeemValue = Boolean(redeemValue && redeemValue > 0)
  const showGetValue = !showRedeemValue && Boolean(getValue && getValue > 0)
  const showAccountValue =
    !showRedeemValue &&
    !showGetValue &&
    Boolean(accountValue && accountValue > 0)

  const handleClick = (e: React.MouseEvent) => {
    onClick?.(e)
  }

  return (
    <WrapperButton
      className={className}
      disabled={!hasInfo}
      variant={variant}
      hasValue={hasValue}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      allowClickPropagation={allowClickPropagation}
    >
      {variant !== 'valueOnly' && (
        <Tag
          variant="success"
          iconEnd={hasInfo ? <Icon name="Information" size="sm" /> : undefined}
        >
          <CashbackTagText id="vioCashLabel">VioCash</CashbackTagText>
        </Tag>
      )}
      {hasValue && (
        <CashbackTagValue variant={variant}>
          <Text variant={variant === 'valueOnly' ? 'titleXS' : 'labelXS'}>
            {showRedeemValue && (
              <FormattedMessageWrapper
                id="redeem"
                defaultMessage="Redeem {value}"
                values={{
                  value: <Value value={value} currency={currency} />
                }}
              />
            )}
            {showGetValue && (
              <FormattedMessageWrapper
                id="get"
                defaultMessage="Get {value}"
                values={{
                  value: <Value value={value} currency={currency} />
                }}
              />
            )}
            {showAccountValue && <Value value={value} currency={currency} />}
          </Text>
        </CashbackTagValue>
      )}
    </WrapperButton>
  )
}
